import '../styles/panda.css';
import '@zillow/constellation-fonts/zillow-fonts.css';
import 'core-js/actual/string/replace-all';
import 'core-js/actual/url/parse';

import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import MobileDetect from 'mobile-detect';
import { useSiftScience } from '@zg-rentals/sift-science';
import { Box } from '@zillow/constellation-10';
import { AppContextProvider } from '../contexts/AppContextProvider';
import pathsManifest from '../utils/pathsManifest';
import { Head } from '@zg-rentals/next/pages';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { DefaultQueryProvider } from '@zg-rentals/react-query5/client';
import AppToastProvider from '../components/AppToastProvider';
import OfflineToast from '../components/OfflineToast';
import { Auth, useAuth } from '../AuthProvider';
import { SatelliteWebSocketWrapper } from '../components/SatelliteWebSocketWrapper';

import { setClient as setZCalendarClient } from '@zg-rentals/ts-z-calendar-client/client';
import { setClient as setSatelliteClient } from '@zg-rentals/ts-satellite-client/client';
import { setClient as setRentalManagerClient } from '@zg-rentals/ts-rental-manager-api-client/client';
import { rentalClient, rentalManagerClient } from '../httpClient';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useComscorePageTracking } from '@zg-rentals/comscore/client';
import { trackComscoreEvent, trackUserEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import StringConstants from '../constants/StringConstants';

const UserAnalytics = ({
  children,
}: {
  children: (arg: { encodedZuid: string; isImpersonator: boolean; isPostingPathAdmin: boolean }) => React.ReactNode;
}) => {
  const { user } = useAuth();
  const router = useRouter();
  const { email, encodedZuid, impersonatorEmail, impersonatorUserToken, isPostingPathAdmin = false } = user ?? {};

  useSiftScience({ email, isImpersonator: Boolean(impersonatorUserToken) });
  useZrmPageViewEvents(trackUserEvent, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useRegisterNotification({ ref: 'SATELLITE_LANDLORD_WEB' });

  const isImpersonator = (!!impersonatorEmail && !!impersonatorUserToken && isPostingPathAdmin) || false;

  return children({ encodedZuid: encodedZuid || '', isImpersonator, isPostingPathAdmin });
};

setZCalendarClient(rentalClient);
setSatelliteClient(rentalClient);
setRentalManagerClient(rentalManagerClient);

export default function MyApp({ Component, pageProps }: AppProps) {
  useBootstrap(() => import('../bootstrap'));

  const paths = pathsManifest();

  const [isClient, setIsClient] = useState(false);
  const [hasMessageListError, setHasMessageListError] = useState<boolean | null>(null);

  const isMobileDevice =
    isClient && typeof window !== 'undefined' ? !!new MobileDetect(window.navigator.userAgent).phone() : false;

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <>
      <Head applicationName="satellite-landlord-web" buildRef={process.env.NEXT_PUBLIC_BUILD_REF} />

      <RpErrorBoundary
        fallback={
          // Renders outside the app shell (no header/footer), so it fills the full viewport
          // rather than using ErrorPageContainer's chrome-aware heights.
          <Box css={{ height: '100dvh' }}>
            <ErrorPage
              errorMessage={StringConstants.SERVER_ERROR_MESSAGE}
              errorDetails={StringConstants.SERVER_ERROR_DETAILS}
              action={<a href={paths.homePage}>{StringConstants.GO_TO_RENTAL_MANAGER}</a>}
              secondaryAction={<a href={paths.helpPage}>{StringConstants.CONTACT_SUPPORT}</a>}
            />
          </Box>
        }
      >
        <DefaultQueryProvider
          defaultOptions={{
            queries: {
              staleTime: 20 * 1000,
            },
          }}
        >
          <Auth.Provider>
            <UserAnalytics>
              {({ isImpersonator, isPostingPathAdmin }) => (
                <SatelliteWebSocketWrapper isMobileDevice={isMobileDevice}>
                  <AppContextProvider
                    value={{
                      isImpersonator,
                      isMobileDevice,
                      isPostingPathAdmin,
                      paths,
                      hasMessageListError,
                      setHasMessageListError,
                    }}
                  >
                    <AppToastProvider>
                      <OfflineToast />
                      <CookiesProvider>
                        <Component {...pageProps} />
                      </CookiesProvider>
                    </AppToastProvider>
                  </AppContextProvider>
                </SatelliteWebSocketWrapper>
              )}
            </UserAnalytics>
          </Auth.Provider>
        </DefaultQueryProvider>
      </RpErrorBoundary>
    </>
  );
}
